import { Vector3 } from '@babylonjs/core';
import { EnemyDistributionConfig } from '../EnemyGenerator';
import { ENEMY_ANIMATIONS, EnemyBehavior } from './EnemyBehavior';

/**
 * Charge Jump Behavior – winds up, then leaps to the predicted player position.
 * Uses measured player velocity to lead-target the landing zone.
 *
 * Config fields:
 *   chargeWindup   – windup duration in seconds (default 1.2)
 *   chargeSpeed    – how fast the jump arc travels (default 12)
 *   detectionRange – range at which charge is triggered (default 25)
 *   attackRange    – AoE landing damage radius (default 2.5)
 */
export class EnemyChargeJumpBehavior extends EnemyBehavior {
  private state: 'idle' | 'windup' | 'jumping' | 'landing' | 'returning' = 'idle';
  private windupTimer: number = 0;
  private landingTimer: number = 0;
  private jumpTarget: Vector3 = Vector3.Zero();
  private jumpOrigin: Vector3 = Vector3.Zero();
  private jumpProgress: number = 0; // 0→1 arc progress
  private jumpDuration: number = 1;
  private landingDuration: number = 0.5;
  private hasDealtDamage: boolean = false;

  // Player velocity tracking
  private prevPlayerPos: Vector3 | null = null;
  private playerVelocity: Vector3 = Vector3.Zero();
  private velocitySmoothTime: number = 0.1; // exponential smoothing window

  constructor(startPosition: Vector3, config: EnemyDistributionConfig) {
    super('enemyChargeJump', startPosition, config);
  }

  behaviorUpdate(deltaTime: number): void {
    if (!this.level?.player) return;

    const windupDuration = this.config.chargeWindup ?? 1.2;
    const speed = this.config.chargeSpeed ?? 12;
    const detRange = this.config.detectionRange ?? 25;
    const impactRadius = this.config.attackRange ?? 2.5;

    // Continuously measure player velocity via finite difference
    const currentPlayerPos = this.getPlayerPosition();
    if (this.prevPlayerPos && deltaTime > 0) {
      const rawVelocity = currentPlayerPos.subtract(this.prevPlayerPos).scale(1 / deltaTime);
      rawVelocity.y = 0; // only horizontal movement matters for prediction
      // Exponential moving average for smoothness
      const alpha = Math.min(1, deltaTime / this.velocitySmoothTime);
      this.playerVelocity = Vector3.Lerp(this.playerVelocity, rawVelocity, alpha);
    }
    this.prevPlayerPos = currentPlayerPos.clone();

    switch (this.state) {
      case 'idle': {
        this.playAnimation(ENEMY_ANIMATIONS.IDLE, true);
        this.snapToGround(deltaTime);
        const dist = this.getPlayerDistance();
        if (dist <= detRange) {
          this.state = 'windup';
          this.windupTimer = 0;
          this.jumpTarget = currentPlayerPos.clone();
          this.lookAt(this.jumpTarget);
        }
        break;
      }
      case 'windup': {
        this.playAnimation(ENEMY_ANIMATIONS.CROUCH, true);
        this.windupTimer += deltaTime;

        // During the first 60% of windup, keep updating target with lead prediction
        if (this.windupTimer < windupDuration * 0.6) {
          // Estimate how long the jump will take given current positions
          const jumpDist = Vector3.Distance(this.mesh.position, currentPlayerPos);
          const estimatedFlightTime = Math.max(0.3, jumpDist / speed) * 0.8;

          // Predict where the player will be when the enemy lands
          this.jumpTarget = currentPlayerPos.add(this.playerVelocity.scale(estimatedFlightTime));

          this.lookAt(this.jumpTarget);
        }
        // Last 40% of windup: target locked in (commitment – can't adjust anymore)

        if (this.windupTimer >= windupDuration) {
          this.state = 'jumping';
          this.jumpOrigin = this.mesh.position.clone();
          const jumpDist = Vector3.Distance(this.jumpOrigin, this.jumpTarget);
          this.jumpDuration = Math.max(0.3, jumpDist / speed);
          this.jumpProgress = 0;
          this.hasDealtDamage = false;
          this.playAnimation(ENEMY_ANIMATIONS.JUMP, false);
        }
        break;
      }
      case 'jumping': {
        this.jumpProgress += deltaTime / this.jumpDuration;
        this.jumpProgress = Math.min(this.jumpProgress, 1);

        // Parabolic arc: lerp XZ, add sin arc for Y
        const t = this.jumpProgress;
        const flatPos = Vector3.Lerp(this.jumpOrigin, this.jumpTarget, t);
        const arcHeight = Math.max(3, Vector3.Distance(this.jumpOrigin, this.jumpTarget) * 0.4);
        flatPos.y += arcHeight * Math.sin(t * Math.PI);
        this.mesh.position.copyFrom(flatPos);

        if (this.jumpProgress >= 1) {
          this.state = 'landing';
          this.landingTimer = 0;
          this.mesh.position.copyFrom(this.jumpTarget);
          this.snapToGround();
          this.playAnimation(ENEMY_ANIMATIONS.ATTACK_MELEE_RIGHT, false);

          // AoE damage check on landing
          if (!this.hasDealtDamage) {
            const distToPlayer = Vector3.Distance(this.mesh.position, this.getPlayerPosition());
            if (distToPlayer <= impactRadius) {
              const event = { normal: new Vector3(0, 1, 0) } as any;
              if (this.level.player.vulnerable && !this.level.game.config.godMode) {
                this.level.player.collide(event, 'Crushed by charge jump!');
              }
            }
            this.hasDealtDamage = true;
            this.level.game.sound.play('enemydestroy', this.mesh);
          }
        }
        break;
      }
      case 'landing': {
        this.landingTimer += deltaTime;
        if (this.landingTimer >= this.landingDuration) {
          this.state = 'returning';
        }
        break;
      }
      case 'returning': {
        this.playAnimation(ENEMY_ANIMATIONS.WALK, true);
        const distToStart = Vector3.Distance(this.mesh.position, this.startPosition);
        if (distToStart > 0.5) {
          this.moveToward(this.startPosition, deltaTime);
        } else {
          this.mesh.position.copyFrom(this.startPosition);
          this.snapToGround(deltaTime);
          this.state = 'idle';
        }
        break;
      }
    }
  }

  clone(): EnemyChargeJumpBehavior {
    return new EnemyChargeJumpBehavior(this.startPosition, this.config);
  }
}
